import { Component, DEFAULT_CURRENCY_CODE, Inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ReceiptType } from '../../../../../core/enum/receipt-type.enum';
import { Receipt } from '../../../../../core/models/payment/receipt';
import { PaymentService } from '../../../../../core/services/api/payment.service';
import { ErrorService } from '../../../../../core/services/error.service';
import { InvestmentService } from '../../../../../core/services/api/investment.service';
import { GtmDataLayerService } from '../../../../../core/services/gtm-data-layer.service';
import { AccountService } from '../../../../../core/services/api/account.service';
import { UserProfile } from '../../../../../core/models/user/user-profile';
import { PaymentType } from 'src/app/core/enum/payment-type.enum';
import { InvestmentStatus } from '../../../../../core/enum/investment-status.enum';

@Component({
    selector: 'app-payment-receipt',
    templateUrl: './payment-receipt.component.html',
    styleUrls: ['./payment-receipt.component.scss'],
    standalone: false
})
export class PaymentReceiptComponent implements OnInit {
  @Input() paymentGatewayName: 'FPX' | 'DuitNow' | 'Other' = 'FPX';
  @Input() viewOnly = false;

  transactionId: string;
  receipt: Receipt;
  isLoading = false;
  receiptType: ReceiptType;
  profile: UserProfile;
  campaignName: string;
  campaignSlug: string;
  ReceiptType = ReceiptType;

  paymentType: PaymentType;
  PaymentType: typeof PaymentType = PaymentType;

  InvestmentStatus: typeof InvestmentStatus = InvestmentStatus;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private errorService: ErrorService,
    private paymentService: PaymentService,
    private investmentService: InvestmentService,
    private _gtmDataLayerService: GtmDataLayerService,
    @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    this.transactionId = this.activatedRoute.snapshot.params.transactionId;
    this.receiptType = this.activatedRoute.snapshot.params.receiptType;

    const currentPath = this.activatedRoute.snapshot?.url[0]?.path;
    if (currentPath.includes('duitnow')) {
      this.paymentGatewayName = 'DuitNow';
    } else if (currentPath.includes('fpx')) {
      this.paymentGatewayName = 'FPX';
    } else {
      this.paymentGatewayName = 'Other';
    }

    this.getProfile();
    this.getTransactionDetail();
  }

  getProfile() {
    this.accountService.profile(true).subscribe({
      next: (profile) => {
        this.profile = profile;
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  getTransactionDetail() {
    this.isLoading = true;

    let observable;
    if (this.receiptType == ReceiptType.Investment) {
      observable = this.paymentService.getTransactionDetail(this.transactionId);
    } else {
      observable = this.paymentService.getTransactionWalletDetails(this.transactionId);
    }

    observable
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (success: Receipt) => {
          this.receipt = success;
          this.getInvestmentDetail(success.refNo);
        },
        error: (err) => {
          const fpxNotFound = err.error?.code == 'FPX_PAYMENT_TRANS_NOT_FOUND';

          if (fpxNotFound) {
            const errorMessage =
              this.paymentGatewayName === 'Other'
                ? 'Payment transaction not found'
                : this.paymentGatewayName + ' payment transaction not found';

            err = { ...err, error: { message: errorMessage } };
          }

          this.errorService.showError(err);
        },
      });
  }

  getInvestmentDetail(sellerOrderNo) {
    this.investmentService.getInvestmentDetail(sellerOrderNo).subscribe({
      next:(data) => {
        const { company, campaign, investment } = data;
        if (!campaign || !investment) return;
        this.campaignName = campaign.name;
        this.campaignSlug = campaign.slug;
        this.paymentType = investment.paymentType;
        this._gtmDataLayerService.logPurchase({
          currency: this.defaultCurrencyCode.trim(),
          value: investment?.pledgedAmount,
          transaction_id: investment?.referenceNumber,
          items: [
            {
              item_name: company?.brandName,
              item_id: campaign?.id,
              item_category: 'ecf',
              price: investment?.pledgedAmount
                ? investment?.pledgedAmount / investment?.lotQuantity
                : 0,
              quantity: investment?.lotQuantity,
            },
          ],
        });

        // Redirect to campaign-specific thank-you page to complete funnel
        const successStatuses = [
          'Successful',
          InvestmentStatus.Paid,
          InvestmentStatus.Completed,
          InvestmentStatus.PaymentApprovalPending,
        ];

        const isSuccess =
          (this.receipt?.transactionStatus && this.receipt.transactionStatus === 'Successful') ||
          (this.receipt?.investmentStatus &&
            successStatuses.includes(Number(this.receipt.investmentStatus)));

        if (this.viewOnly && isSuccess && this.campaignSlug && investment?.referenceNumber) {
          this.router.navigate(['/equity', this.campaignSlug, 'thank-you'], {
            queryParams: {
              ref: investment.referenceNumber,
              paymentReferenceNo: investment.referenceNumber,
              campaignStatus: campaign.campaignStatus,
            },
          });
        }
      },
      error:(err) => {
        this.errorService.showError(err);
      },
    });
  }

  print() {
    window.print();
  }

  private getPaymentTypeString(type: PaymentType): string {
    switch (type) {
      case PaymentType.MaybankFPX:
        return 'paymentType.MaybankFPX';
      case PaymentType.Cheque:
        return 'paymentType.Cheque';
      case PaymentType.Swift:
        return 'paymentType.Swift';
      case PaymentType.BankIn:
        return 'paymentType.BankIn';
      case PaymentType.DuitNow:
        return 'paymentType.DuitNow';
      case PaymentType.Wallet:
        return 'paymentType.Wallet';
      default:
        return '-';
    }
  }

  getPaymentTypeDisplay(): string {
    return this.getPaymentTypeString(this.paymentType);
  }

  getTransactionStatus(): string {
    if (this.receipt?.transactionStatus) {
      return this.receipt.transactionStatus;
    }

    if (this.receipt?.investmentStatus) {
      switch (Number(this.receipt.investmentStatus)) {
        case InvestmentStatus.PendingPayment:
          return 'investment.PendingPayment';
        case InvestmentStatus.Paid:
          return 'investment.Paid';
        case InvestmentStatus.Cancelled:
          return 'investment.Cancelled';
        case InvestmentStatus.Refunded:
          return 'investment.Refunded';
        case InvestmentStatus.RefundRequested:
          return 'investment.RefundRequested';
        case InvestmentStatus.PaymentApprovalPending:
          return 'investment.PaymentApprovalPending';
        case InvestmentStatus.Completed:
          return 'investment.Completed';
        case InvestmentStatus.PledgeCancelled:
          return 'investment.PledgeCancelled';
        default:
          return '-';
      }
    }

    return this.getPaymentTypeString(this.paymentType);
  }

  isPrimaryColour(): boolean {
    if (this.receipt?.transactionStatus === 'Successful') {
      return true;
    }

    const successStatuses = [
      InvestmentStatus.Paid,
      InvestmentStatus.Completed,
      InvestmentStatus.PaymentApprovalPending,
      InvestmentStatus.PendingPayment,
    ];

    return successStatuses.includes(Number(this.receipt?.investmentStatus));
  }
}
